import { LDate } from '@app/components/date/LDate';
import { LTime } from '@app/components/date/LTime';
import { getTopicDisplayTitle } from '@app/modules/logger/screens/stream/features/live-topics/helpers/get-topic-display-title';
import { nlpIntegration } from '@mi-front/api-clients';
import { toDateStr } from '@mi-front/core';
import HistoryIcon from '@mui/icons-material/History';
import { Box, CircularProgress, Link, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useGoToAssociatedTopicTime } from './useGoToAssociatedTopicTime';

const TITLE_MAX_WIDTH = 280;

/**
 * Renders a link that loads the associated topic by id and, when clicked,
 * navigates the user to that topic's start time (in-place or via cursor query param).
 * Text: "Viene de: " + spinner while loading, or associated topic title (ellipsis if long).
 * At the end: time only when the associated topic is on the same date as the child topic, otherwise date + time.
 */
export function AssociatedTopic({
  associatedTopicId,
  topic: childTopic,
  onClose,
}: {
  associatedTopicId: string;
  topic: nlpIntegration.TopicOutDto;
  onClose?: () => void;
}) {
  const { $t } = useIntl();
  const {
    isLoading,
    topic: loadedTopic,
    goToTopicTime,
  } = useGoToAssociatedTopicTime(associatedTopicId, onClose);

  const comesFromLabel = $t({
    id: 'logger.streamScreen.liveTopic.comesFrom',
    defaultMessage: 'Viene de: ',
  });

  const sameDayAsChild = useMemo(() => {
    if (!loadedTopic?.start_time || !childTopic?.start_time) {
      return false;
    }

    const associatedDate = toDateStr(loadedTopic.start_time);
    const childDate = toDateStr(childTopic.start_time);
    return associatedDate === childDate;
  }, [loadedTopic?.start_time, childTopic?.start_time]);

  const handleClick = () => {
    if (loadedTopic) {
      goToTopicTime();
    }
  };

  return (
    <Box sx={{ mt: 1, mb: 0.5 }}>
      <Link
        component="button"
        type="button"
        variant="caption"
        onClick={handleClick}
        disabled={!loadedTopic}
        sx={{
          cursor: loadedTopic ? 'pointer' : 'default',
          textAlign: 'left',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          textDecoration: 'none',
        }}
      >
        <HistoryIcon sx={{ fontSize: 14, mt: '-2px' }} />
        {comesFromLabel}
        {isLoading ? (
          <CircularProgress size={10} color="inherit" />
        ) : (
          <Typography
            component="span"
            variant="caption"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: TITLE_MAX_WIDTH,
            }}
          >
            {loadedTopic ? getTopicDisplayTitle(loadedTopic) : ''}
          </Typography>
        )}
        {loadedTopic?.start_time && (
          <Box component="span" sx={{ flexShrink: 0 }}>
            (
            {sameDayAsChild ? (
              <LTime value={loadedTopic.start_time} hour12={false} />
            ) : (
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                <LDate value={loadedTopic.start_time} day="2-digit" month="2-digit" />
                <LTime value={loadedTopic.start_time} hour12={false} />
              </Box>
            )}
            )
          </Box>
        )}
      </Link>
    </Box>
  );
}
